import React, { useState, useEffect, useRef, forwardRef, useMemo, useCallback } from 'react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableCard from '../SortableCard';
import KanbanCard from '../KanbanCard';
import SwimlaneColumnCell from './SwimlaneColumnCell';

// ═══════════════════════════════════════════════════════════════
// COLOR BLENDING UTILITIES
// ═══════════════════════════════════════════════════════════════

/**
 * Parse a hex color to RGB values
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  } : null;
};

/**
 * Blend a color with alpha over a base color
 * Formula: result = base * (1 - alpha) + overlay * alpha
 */
const blendColors = (baseRgb, overlayRgb, alpha) => ({
  r: Math.round(baseRgb.r * (1 - alpha) + overlayRgb.r * alpha),
  g: Math.round(baseRgb.g * (1 - alpha) + overlayRgb.g * alpha),
  b: Math.round(baseRgb.b * (1 - alpha) + overlayRgb.b * alpha),
});

/**
 * Calculate the flattened background color for swimlane columns
 * Layers: page bg -> swimlane tint (no column overlay - using borders instead)
 */
const calculateFlattenedBg = (swimlaneColor) => {
  // Page background: #0a0a0c (matches kanban-view)
  const pageBg = { r: 10, g: 10, b: 12 };
  
  let result = pageBg;
  
  // Swimlane tint: swimlaneColor at ~8% opacity (0x15 = 21/255 ≈ 0.082)
  if (swimlaneColor) {
    const swimlaneRgb = hexToRgb(swimlaneColor);
    if (swimlaneRgb) {
      const swimlaneAlpha = 0.082; // 15 in hex = ~8.2%
      result = blendColors(result, swimlaneRgb, swimlaneAlpha);
    }
  }
  
  // No column overlay - using thin border lines instead
  return `rgb(${result.r}, ${result.g}, ${result.b})`;
};

/**
 * Calculate the flattened background for swimlane label cell (no column overlay)
 * Layers: page bg -> swimlane tint only
 */
const calculateLabelBg = (swimlaneColor) => {
  // Page background: #0a0a0c (matches kanban-view)
  const pageBg = { r: 10, g: 10, b: 12 };
  
  let result = pageBg;
  
  // Swimlane tint: swimlaneColor at ~8% opacity (0x15 = 21/255 ≈ 0.082)
  if (swimlaneColor) {
    const swimlaneRgb = hexToRgb(swimlaneColor);
    if (swimlaneRgb) {
      const swimlaneAlpha = 0.082; // 15 in hex = ~8.2%
      result = blendColors(result, swimlaneRgb, swimlaneAlpha);
    }
  }
  
  return `rgb(${result.r}, ${result.g}, ${result.b})`;
};

// ═══════════════════════════════════════════════════════════════
// SWIMLANE SETTING LABELS
// ═══════════════════════════════════════════════════════════════

const SWIMLANE_LABELS = {
  tag: 'primary tag',
  priority: 'priority',
  assignee: 'assignee',
  dueDate: 'due date',
};

/**
 * Generate a descriptive empty message for a swimlane column cell
 */
const getEmptyMessage = (columnName, swimlaneLabel, swimlaneSetting) => {
  const groupLabel = SWIMLANE_LABELS[swimlaneSetting] || 'group';
  return `No ${columnName} missions with ${groupLabel}: ${swimlaneLabel}`;
};

// ═══════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════

const ChevronIcon = ({ isExpanded }) => (
  <svg 
    width="10" 
    height="10" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    style={{ 
      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/**
 * SwimlaneRow Component (Option B - Label on left only)
 * 
 * A horizontal swimlane with:
 * - Sticky label cell on left (clickable to collapse/expand)
 * - Columns of cards to the right
 * - No full-width header bar
 */
const SwimlaneRow = forwardRef(({
  swimlane, // { id, label, color, tasks }
  columns, // Status columns
  collapsedColumns = new Set(), // Set of collapsed column IDs
  expandingColumns = new Set(), // Set of columns currently expanding (for animation)
  tasksByStatus, // Tasks grouped by status within this swimlane
  swimlaneSetting = 'tag', // The swimlane grouping type (tag, priority, etc.)
  categories,
  allTags,
  allTasks,
  nestedSprints,
  statuses,
  selectedTaskIds,
  activeCardId,
  activeColumnId, // Currently dragged column ID (for animation sync)
  isAnyColumnDragging = false, // Performance optimization during column drag
  isAnyCardDragging = false, // PERFORMANCE: Virtualize cards during card drag too
  isCardDraggingInOtherSwimlane = false, // Performance optimization: hide non-visible cards when card is dragged in another swimlane
  isBelowVisibleArea = false, // More aggressive optimization: hide ALL cards for swimlanes below viewport
  onTaskClick,
  onOpenDetails,
  onOpenAttachments,
  onCategoryChange,
  onRenameCategory,
  onDeleteCategory,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  onOpenNestedSprint,
  onCreateNestedSprint,
  onRemoveNestedSprint,
  onIconChange,
  onRename,
  onStatusChange,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  onPriorityChange,
  onDifficultyChange,
  onCustomFieldValueChange,
  customFields = [],
  onDeleteTask,
  onCopyCard,
  onDuplicateCard,
  onCreateParallelMission,
  onInsertMissionBefore,
  onSetStatusAutoSync,
  onDeliverablesChange,
  onInputsChange,
  onArchiveTask,
  onUnarchiveTask,
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  lockStates,
  cardDisplaySettings,
  editingCardId,
  onFinishTitleEdit,
  defaultExpanded = true,
  // View navigation (for "Open in view" button)
  currentView,
  customViews,
  activeCustomViewId,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Deliverables warning modal
  onSubmitDeliverable,
  // Deliverables editing (for conditions modal)
  onEditDeliverables,
  onClearDeliverable,
  onNavigateToParentMission,
  // Template mode
  isTemplate = false,
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isExpanding, setIsExpanding] = useState(false);
  const wasExpandedRef = useRef(defaultExpanded);

  // Track expand transitions to trigger animation only when expanding from collapsed
  useEffect(() => {
    if (isExpanded && !wasExpandedRef.current) {
      // Just expanded from collapsed - trigger animation
      setIsExpanding(true);
      const timer = setTimeout(() => setIsExpanding(false), 350); // Animation duration + buffer
      return () => clearTimeout(timer);
    }
    wasExpandedRef.current = isExpanded;
  }, [isExpanded]);

  // Count total tasks in this swimlane
  const totalTasks = swimlane.tasks?.length || 0;

  // Get tasks for each column within this swimlane
  const getColumnTasks = (columnId) => {
    return tasksByStatus[columnId] || [];
  };

  // Create background tint from swimlane color
  // Compute flattened backgrounds for opaque elements (sticky label, gradient indicators)
  const flattenedBg = useMemo(() => calculateFlattenedBg(swimlane.color), [swimlane.color]);
  const labelBg = useMemo(() => calculateLabelBg(swimlane.color), [swimlane.color]);
  
  const swimlaneStyle = swimlane.color ? {
    '--swimlane-color': swimlane.color,
    '--swimlane-tint': `${swimlane.color}15`, // ~8% opacity (0x15 = 21/255)
    '--swimlane-flattened-bg': flattenedBg, // Fully opaque blended color for column gradients
    '--swimlane-label-bg': labelBg, // Fully opaque blended color for label cell
  } : {
    '--swimlane-flattened-bg': 'rgb(10, 10, 12)', // Default: just page bg (#0a0a0c) - no column overlay
    '--swimlane-label-bg': 'rgb(10, 10, 12)', // Default: just page bg (#0a0a0c)
  };

  const rowClasses = [
    'swimlane-row',
    isExpanded ? 'swimlane-row--expanded' : 'swimlane-row--collapsed',
    isExpanding && 'swimlane-row--expanding',
  ].filter(Boolean).join(' ');

  // Render a single card
  const renderCard = (task) => {
    const category = task.category
      ? (allTags.find(t => t.name === (Array.isArray(task.category) ? task.category[0] : task.category)) ||
         categories.find(c => c.name === (Array.isArray(task.category) ? task.category[0] : task.category)))
      : null;
    const nestedSprint = task.nestedSprintId
      ? nestedSprints.find(s => s.id === task.nestedSprintId)
      : null;

    return (
      <SortableCard key={task.id} id={task.id} swimlaneId={swimlane.id}>
        <KanbanCard
          task={task}
          category={category}
          allCategories={categories}
          allTags={allTags}
          allTasks={allTasks}
          nestedSprint={nestedSprint}
          statuses={statuses}
          isSelected={selectedTaskIds.has(task.id)}
          isDragging={activeCardId === task.id}
          isEditingTitle={editingCardId === task.id}
          onFinishTitleEdit={onFinishTitleEdit}
          onSelect={onTaskClick}
          onOpenDetails={onOpenDetails}
          onOpenAttachments={onOpenAttachments}
          onCategoryChange={onCategoryChange}
          onRenameCategory={onRenameCategory}
          onDeleteCategory={onDeleteCategory}
          onAddTag={onAddTag}
          onRemoveTag={onRemoveTag}
          onSetPrimaryTag={onSetPrimaryTag}
          onRenameTag={onRenameTag}
          onDeleteTag={onDeleteTag}
          onOpenNestedSprint={onOpenNestedSprint}
          onCreateNestedSprint={onCreateNestedSprint}
          onRemoveNestedSprint={() => onRemoveNestedSprint?.(task.id)}
          onIconChange={onIconChange}
          onRename={onRename}
          onStatusChange={onStatusChange}
          onDueDateChange={onDueDateChange}
          onRemindersChange={onRemindersChange}
          onEstimatedDurationMinutesChange={onEstimatedDurationMinutesChange}
          onCreatedAtChange={onCreatedAtChange}
          onStartedAtChange={onStartedAtChange}
          onCompletedAtChange={onCompletedAtChange}
          onUpdatedAtChange={onUpdatedAtChange}
          onPriorityChange={onPriorityChange}
          onDifficultyChange={onDifficultyChange}
          onCustomFieldValueChange={onCustomFieldValueChange}
          customFields={customFields}
          onDeleteTask={onDeleteTask}
          onCopyCard={onCopyCard}
          onDuplicateCard={onDuplicateCard}
          onCreateParallelMission={onCreateParallelMission}
          onInsertMissionBefore={onInsertMissionBefore}
          onSetStatusAutoSync={onSetStatusAutoSync}
          onDeliverablesChange={onDeliverablesChange}
          onInputsChange={onInputsChange}
          onArchiveTask={onArchiveTask}
          onUnarchiveTask={onUnarchiveTask}
          onDecisionAnswerChange={onDecisionAnswerChange}
          onDecisionOptionsChange={onDecisionOptionsChange}
          onAddStatus={onAddStatus}
          onDeleteStatus={onDeleteStatus}
          onRenameStatus={onRenameStatus}
          lockState={lockStates?.[task.id]}
          cardDisplaySettings={cardDisplaySettings}
          isTemplate={isTemplate}
          currentView={currentView}
          customViews={customViews}
          activeCustomViewId={activeCustomViewId}
          onViewChange={onViewChange}
          onSelectCustomView={onSelectCustomView}
          onGoToTaskInView={onGoToTaskInView}
          onSubmitDeliverable={onSubmitDeliverable}
          onEditDeliverables={onEditDeliverables}
          onClearDeliverable={onClearDeliverable}
          onNavigateToParentMission={onNavigateToParentMission}
        />
      </SortableCard>
    );
  };

  return (
    <div 
      ref={ref}
      className={rowClasses}
      style={swimlaneStyle}
    >
      {/* Swimlane Content - Label cell + columns (no header bar) */}
      <div className="swimlane-row__content">
        {/* Sticky label cell on left - clickable to collapse/expand */}
        <div 
          className="swimlane-row__label-cell"
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? 'Collapse swimlane' : 'Expand swimlane'}
        >
          {/* Inner wrapper sticks to top while scrolling vertically */}
          <div className="swimlane-row__label-inner">
            <span className="swimlane-row__toggle">
              <ChevronIcon isExpanded={isExpanded} />
            </span>
            
            {swimlane.color && (
              <span 
                className="swimlane-row__color-dot"
                style={{ backgroundColor: swimlane.color }}
              />
            )}
            
            <span className="swimlane-row__label-text">
              {swimlane.label}
            </span>
            
            <span className="swimlane-row__count">
              {totalTasks}
            </span>
          </div>
        </div>

        {/* Column cells with cards - always rendered for smooth animation */}
        {columns.map((column, index) => {
          const columnTasks = getColumnTasks(column.id);
          const columnTaskIds = columnTasks.map(t => t.id);
          const isColumnCollapsed = collapsedColumns.has(column.id);
          const isColumnExpanding = expandingColumns.has(column.id);

          return (
            <SwimlaneColumnCell
              key={column.id}
              columnId={column.id}
              columnIndex={index}
              totalColumns={columns.length}
              activeColumnId={activeColumnId}
              swimlaneId={swimlane.id}
              isCollapsed={isColumnCollapsed}
              isExpanding={isColumnExpanding}
              isSwimlaneCollapsed={!isExpanded}
              hasCards={columnTasks.length > 0}
              isAnyColumnDragging={isAnyColumnDragging}
              isAnyCardDragging={isAnyCardDragging}
              isCardDraggingInOtherSwimlane={isCardDraggingInOtherSwimlane}
              isBelowVisibleArea={isBelowVisibleArea}
              totalCardCount={columnTasks.length}
            >
              {/* Only show cards if column is not collapsed AND not below visible area */}
              {/* For swimlanes below viewport, hide ALL cards (aggressive optimization) */}
              {/* For other swimlanes, SwimlaneColumnCell handles filtering non-visible cards */}
              {!isColumnCollapsed && columnTasks.length > 0 && !isBelowVisibleArea && (
                <SortableContext 
                  items={columnTaskIds} 
                  strategy={verticalListSortingStrategy}
                >
                  {columnTasks.map(task => renderCard(task))}
                </SortableContext>
              )}

              {/* Empty state message - also hide for swimlanes below visible area */}
              {!isColumnCollapsed && columnTasks.length === 0 && !isBelowVisibleArea && (
                <span className="swimlane-row__empty-text">
                  {getEmptyMessage(column.name, swimlane.label, swimlaneSetting)}
                </span>
              )}
            </SwimlaneColumnCell>
          );
        })}
      </div>
    </div>
  );
});

SwimlaneRow.displayName = 'SwimlaneRow';

export default SwimlaneRow;

